import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import EventCard from '../components/EventCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkle, ClockClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';

export default function Organizer() {
  const { user, login } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    if (user) api.get('/me/submissions').then(r => setSubmissions(r.data));
    // Stripe return polling
    const sid = params.get('session_id');
    if (sid && params.get('payment') === 'success') {
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === 'paid') {
            toast.success('Payment confirmed — your event is featured!');
            api.get('/me/submissions').then(r => setSubmissions(r.data));
            return;
          }
        } catch {}
        if (attempts < 5) setTimeout(poll, 2000);
      };
      poll();
    }
  }, [user, params]);

  if (!user) return (
    <>
      <Seo path="/organizer" title="Organizer Portal" noindex />
      <div className="max-w-2xl mx-auto p-24 text-center">
        <h1 className="font-display text-4xl mb-4">Sign in to view your submissions</h1>
        <Button onClick={login} className="bg-forest hover:bg-forest/90 text-bone rounded-full">Sign in</Button>
      </div>
    </>
  );

  const featurePay = async (event_id) => {
    try {
      const r = await api.post('/payments/feature-checkout', { event_id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error('Could not start checkout');
    }
  };

  const StatusBadge = ({ s }) => {
    const map = {
      approved: { c: 'bg-forest text-bone', i: <CheckCircle size={14} weight="fill" /> },
      pending: { c: 'bg-sand text-ink', i: <ClockClockwise size={14} weight="fill" /> },
      rejected: { c: 'bg-destructive text-white', i: <XCircle size={14} weight="fill" /> },
    };
    const m = map[s] || map.pending;
    return <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold uppercase ${m.c}`}>{m.i} {s}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo path="/organizer" title="Organizer Portal" noindex />
      <p className="label text-terracotta mb-2">Organizer</p>
      <h1 className="font-display text-4xl mb-2">Your submissions</h1>
      <p className="text-muted-foreground mb-10">Track approval status and boost your listings.</p>

      {submissions.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <p className="text-muted-foreground mb-4">No submissions yet.</p>
          <Link to="/submit"><Button className="bg-forest text-bone rounded-full">Submit a pow wow</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map(e => (
            <div key={e.event_id} className="bg-card border border-border rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`submission-${e.event_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <StatusBadge s={e.status} />
                  {e.featured && <span className="bg-sand text-ink text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"><Sparkle size={10} className="inline" weight="fill" /> Featured</span>}
                </div>
                <h3 className="font-display text-xl">{e.name}</h3>
                <p className="text-sm text-muted-foreground">{e.city}, {e.state} · {e.start_date}</p>
              </div>
              <div className="flex gap-2">
                {e.status === 'approved' && !e.featured && (
                  <Button onClick={() => featurePay(e.event_id)} className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full" data-testid={`feature-${e.event_id}`}>
                    <Sparkle size={16} weight="fill" className="mr-1" /> Feature ($99)
                  </Button>
                )}
                {e.status === 'approved' && (
                  <Link to={`/events/${e.event_id}`}><Button variant="outline" className="rounded-full">View</Button></Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
